import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { validators } from '../../../validators/validators'
import { selectIsEditingMode, selectShipment, selectShipmentAccordions, setAccordions } from '../shipmentSlice'
import SelectCity, { selectCityInputLanguages } from '../../citySelect/SelectCity'

import style from '../../../sass/pages/shipment/shipment.module.scss'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { interpretEmailError, interpretReceiverAddressError } from '../../../validators/interpretators'
import PhoneInput from 'react-phone-input-2'
import { useTranslation } from 'react-i18next'
import { isB2BPackage, isDocumentsPackage } from '../../../helpers/packageTypesHelper/packageTypesHelper'
import { getLocationUnitLangPostfix } from './editShipmentUtils'
import { selectCountries } from '../../citySelect/selectCitySlice'

const Receiver = ({ state, setState, errors, setErrors }) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const isEditing = useSelector(selectIsEditingMode);
    const accordionState = useSelector(selectShipmentAccordions);
    const shipment = useSelector(selectShipment);

    const countries = useSelector(selectCountries);
    const [receiverGeolocation, setReceiverGeolocation] = useState({
        city: "",
        district: "",
        area: "",
        country: "",
    })

    //setting geolocation from shipment
    useEffect(() => {
        if(shipment) {
            setReceiverGeolocation({
                city: shipment["receiver-city-tr"],
                district: shipment["receiver-district-tr"],
                area: shipment["receiver-area-tr"],
                country: shipment["receiver-country-tr"],
            })
        }
    }, [i18n.language, setReceiverGeolocation, shipment])
    
    //handling geolocation change
    useEffect(() => {
        if (receiverGeolocation.country) {
            let hasCities = false;

            const country = countries.find(
                (country) => country["ru-name"] === receiverGeolocation.country || 
                country["tr-name"] === receiverGeolocation.country
            );

            if (country) {
                hasCities = country["has-cities"];
            }
            setState(prev => ({
                ...prev,
                "receiver-country": receiverGeolocation.country,
                "receiver-city": receiverGeolocation.city,
                "receiver-district": receiverGeolocation.district,
                "receiver-area": receiverGeolocation.area,
                countryHasCities: hasCities,
            }));
        }
    }, [countries, receiverGeolocation, setState])

    const setName = (value) => {
        setState((prev) => ({ ...prev, "receiver-name": value }));

        if (validators.HasOnlyEnglishLettersSymbolsAndSpecialSigns(value) || 
            validators.HasOnlyLettersAndSpaces(value)) {
            setErrors(prev => ({
                ...prev,
                "receiver-name": t("errors.receiver_name.only_english_letters"),
            }))
            return
        }
        if (validators.ValidateLength(value, 2)) {
            setErrors(prev => ({
                ...prev,
                "receiver-name": t("errors.receiver_name.min_2")
            }))
            return
        }
        setErrors(prev => ({
            ...prev,
            "receiver-name": ""
        }))
    }

    const setReceiverCompany = (value) => {
        setState((prev) => ({ ...prev, "receiver-company": value }));

        if (validators.HasOnlyEnglishLettersSymbolsAndSpecialSigns(value)) {
            setErrors((prev) => ({
                ...prev,
                "receiver-company": t("errors.receiver_company.only_english_letters")
            }));
            return
        }

        if (value.length > 250) {
            setErrors((prev) => ({
                ...prev,
                "receiver-company": t("errors.receiver_company.max_250")
            }));
            return
        }

        setErrors((prev) => ({
            ...prev,
            "receiver-company": ""
        }));
    }

    const setAddress = (value) => {
        setState((prev) => ({ ...prev, "receiver-address": value }));

        let validationResult = interpretReceiverAddressError(validators.ValidateReceiverAddress(value, 10, 90))
        if (validationResult != null) {
            setErrors(prev => ({
                ...prev,
                "receiver-address": t(validationResult)
            }))
        } else {
            setErrors(prev => ({
                ...prev,
                "receiver-address": ""
            }))
        }
    }

    const setPhoneNumber = (value) => {
        setState((prev) => ({ ...prev, "receiver-phone-number": value }));

        let validationResult = validators.ValidatePhone(value)
        if (validationResult != null) {
            setErrors(prev => ({
                ...prev,
                "receiver-phone-number": t("errors.phone.invalid_format")
            }))
            return
        }

        setErrors(prev => ({
            ...prev,
            "receiver-phone-number": ""
        }))
    }

    const setEmail = (value) => {
        setState((prev) => ({ ...prev, "receiver-email": value }));

        let validationResult = interpretEmailError(validators.ValidateEmail(value))
        if (validationResult != null) {
            setErrors(prev => ({
                ...prev,
                "receiver-email": t(validationResult),
            }))
        } else {
            setErrors(prev => ({
                ...prev,
                "receiver-email": ""
            }))
        }
    }

    const setPostalIndex = (value) => {
        setState((prev) => ({ ...prev, "receiver-postal-index": value }));
    }

    const setDescription = (value) => {
        setState((prev) => ({ ...prev, "receiver-description": value }))
    };


    const handleAccordionChange = () => {
        dispatch(setAccordions({
            additionalOpened: accordionState.additionalOpened,
            receiverOpened: !accordionState.receiverOpened,
        }))
    }


    return (
        <div className='flex flex-col gap-2 w-full md:w-6/12'>
            <Accordion
                expanded={accordionState.receiverOpened}
                className={
                    accordionState.additionalOpened && accordionState.receiverOpened
                        ? "h-full"
                        : ""
                }
                onChange={handleAccordionChange}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <span className='text-emerald-600 text-lg font-bold' >
                        {t("shipment.shipment_data.receiver.title")}
                    </span>
                </AccordionSummary>
                <AccordionDetails className='flex flex-col gap-3'>
                    {/* RECEIVER NAME */}
                    <div
                        className='grid grid-cols-10 xs:grid-cols-12 items-center text-2xs font-bold'
                    >
                        <div className='col-span-8 xs:col-span-4 text-left'>
                            {t("shipment.shipment_data.receiver.receiver_name.title")}:
                        </div>
                        <input
                            type="text"
                            disabled={!isEditing}
                            className={`${style.input} col-span-10 xs:col-span-8 font-normal`}
                            value={state["receiver-name"]}
                            onInput={(e) => setName(e.target.value)}
                        />
                        {errors["receiver-name"] &&
                            <div
                                className={`${style.error} col-span-10 text-left font-normal`}
                            >{errors["receiver-name"]}</div>}
                    </div>
                    {/* COMPANY NAME */}
                    {(isDocumentsPackage(state["package-type"]) || isB2BPackage(state["package-type"])) &&
                        <div
                            className='grid grid-cols-10 xs:grid-cols-12 items-center text-2xs font-bold'
                        >
                            <div className='col-span-8 xs:col-span-4 text-left'>
                                {t("shipment.shipment_data.receiver.company.title")}: </div>
                            <input
                                type="text"
                                disabled={!isEditing}
                                className={`${style.input} col-span-10 xs:col-span-8 font-normal`}
                                value={state["receiver-company"]}
                                onInput={(e) => setReceiverCompany(e.target.value)}
                            />
                            {errors["receiver-company"] &&
                                <div
                                    className={`${style.error} col-span-10 text-left font-normal`}
                                >{errors["receiver-company"]}</div>}
                        </div>
                    }
                    {/* EMAIL */}
                    <div
                        className='grid grid-cols-10 xs:grid-cols-12 items-center text-2xs font-bold'
                    >
                        <div className='col-span-8 xs:col-span-4 text-left'>
                            {t("shipment.shipment_data.receiver.email.title")}:
                        </div>
                        <input
                            type="text"
                            disabled={!isEditing}
                            className={`${style.input} col-span-10 xs:col-span-8 font-normal`}
                            value={state["receiver-email"]}
                            onInput={(e) => setEmail(e.target.value)}
                        />
                        {errors["receiver-email"] &&
                            <div
                                className={`${style.error} col-span-10 text-left font-normal`}
                            >{errors["receiver-email"]}</div>}
                    </div>
                    {/* PHONE NUMBER */}
                    <div
                        className='grid grid-cols-10 xs:grid-cols-12 items-center text-2xs font-bold'
                    >
                        <div className='col-span-8 xs:col-span-4 text-left'>
                            {t("shipment.shipment_data.receiver.phone.title")}:
                        </div>
                        <PhoneInput
                            disabled={!isEditing}
                            value={state["receiver-phone-number"]}
                            onChange={(e) => setPhoneNumber(e)}
                            inputClass={`${style.input} font-normal`}
                            specialLabel=""
                            className="col-span-10 xs:col-span-8"
                        />
                        {errors["receiver-phone-number"] &&
                            <div
                                className={`${style.error} col-span-10 text-left font-normal`}
                            >{errors["receiver-phone-number"]}</div>}
                    </div>
                    {/* LOCATION */}
                    <SelectCity
                        disabled={!isEditing}
                        state={receiverGeolocation}
                        setState={setReceiverGeolocation}
                        containerClassName={`flex flex-col gap-2 w-full`}
                        inputBlockClassNames={"grid grid-cols-10 xs:grid-cols-12 items-center text-2xs font-bold"}
                        labelClassNames={"col-span-8 xs:col-span-4 text-left"}
                        selectClassName={`${style.select} col-span-10 xs:col-span-8 font-medium`}
                        inputsClassNames={`${style.input} col-span-10 xs:col-span-8 font-normal`}
                        language={selectCityInputLanguages.TR}
                    />
                    {state.countryHasCities && !state["receiver-city"] &&
                        <div
                            className={`${style.error} col-span-10 text-left font-normal`}
                        >
                            {t("shipment.shipment_data.receiver.select_city.errors.required")}
                        </div>
                    }
                    {/* ADDRESS */}
                    <div
                        className='grid grid-cols-10 xs:grid-cols-12 items-center text-2xs font-bold'
                    >
                        <div className='col-span-8 xs:col-span-4 text-left'>
                            {t("shipment.shipment_data.receiver.address.title")}:
                        </div>
                        <input
                            type="text"
                            disabled={!isEditing}
                            className={`${style.input} col-span-10 xs:col-span-8 font-normal`}
                            value={state["receiver-address"]}
                            onInput={(e) => setAddress(e.target.value)}
                        />
                        {errors["receiver-address"] &&
                            <div
                                className={`${style.error} col-span-10 text-left font-normal`}
                            >{errors["receiver-address"]}</div>}
                    </div>
                    {/* POSTAL INDEX */}
                    <div
                        className='grid grid-cols-10 xs:grid-cols-12 items-center text-2xs font-bold'
                    >
                        <div className='col-span-8 xs:col-span-4 text-left'>
                            {t("shipment.shipment_data.receiver.postal_index.title")}:
                        </div>
                        <input
                            type="text"
                            disabled={!isEditing}
                            className={`${style.input} col-span-10 xs:col-span-8 font-normal`}
                            value={state["receiver-postal-index"]}
                            onInput={(e) => setPostalIndex(e.target.value)}
                        />
                        {errors["receiver-postal-index"] &&
                            <div
                                className={`${style.error} col-span-10 text-left font-normal`}
                            >{errors["receiver-postal-index"]}</div>}
                    </div>

                    {/* DESCRIPTION */}
                    <div
                        className='grid grid-cols-10 xs:grid-cols-12 items-center text-2xs font-bold'
                    >
                        <div className='col-span-8 xs:col-span-4 text-left'>
                            {t("shipment.shipment_data.receiver.description.title")}:
                        </div>
                        <textarea
                            columns={30}
                            rows={3}
                            type="text"
                            disabled={!isEditing}
                            className={`${style.input} col-span-10 xs:col-span-8 font-normal`}
                            value={state["receiver-description"]}
                            onInput={e => setDescription(e.target.value)}
                        />
                        {errors["receiver-description"] &&
                            <div
                                className={`${style.error} col-span-10 text-left font-normal`}
                            >{errors["receiver-description"]}</div>}
                    </div>
                </AccordionDetails>
            </Accordion>
        </div>
    )
}

export default Receiver